import useAsyncControl from '@onepercentio/one-ui/hooks/useAsyncControl'
import useQuery from '@onepercentio/one-ui/hooks/utility/useQuery'
import { Sale } from 'core/logic/asset/asset.types'
import { OfferType } from 'core/logic/drop/drop.types'
import { useTenant } from 'core/logic/tenant/tenant.hook'
import {
  PaymentProviders,
  PaymentsProvider,
} from 'core/logic/tenant/tenant.types'
import {
  doMarketplacePurchase,
  doPurchase,
} from 'core/modules/firebase/service'
import { CurrenciesEnum } from 'core/types/CurrenciesEnum'
import { PaymentMethods } from 'pages/Authenticated/Payment/Payment.types'
import { useCallback, useMemo } from 'react'

export type MethodNProvider = [
  method: PaymentMethods,
  provider: PaymentsProvider
]

const parsePaymentMethods = (
  paymentProviders?: PaymentProviders
): MethodNProvider[] => {
  if (!paymentProviders) {
    return []
  }
  return Object.entries(paymentProviders).reduce<MethodNProvider[]>(
    (methods, [provider, props]) => {
      if (props.active) {
        return [
          ...methods,
          ...Object.entries(props.methods || {}).reduce<MethodNProvider[]>(
            (m, [method, activeOrObj]) => [
              ...m,
              ...((
                typeof activeOrObj === 'object'
                  ? activeOrObj.active
                  : activeOrObj
              )
                ? [[method, provider] as MethodNProvider]
                : []),
            ],
            []
          ),
        ]
      }
      return methods
    },
    []
  )
}

export function usePaymentMethods() {
  const { tenant } = useTenant()
  const paymentMethods = useMemo(
    () => parsePaymentMethods(tenant?.paymentProviders),
    [tenant?.paymentProviders]
  ).sort()
  return paymentMethods
}

export function useDoPayment() {
  const amount = useQuery<{ amount: string }>().get('amount') ?? '1'
  const doPayment = useCallback(async function doPayment(
    offer: OfferType,
    method: PaymentMethods,
    provider: PaymentsProvider,
    currency: CurrenciesEnum,
    user: {
      email: string
      name: string
      document: string
      isForeign: boolean
    }
  ) {
    const sanitizedCpf = user.document
      ? user.document.replace(/[^0-9]/g, '')
      : ''
    return await doPurchase({
      offerId: offer.id,
      tenantId: process.env.REACT_APP_TENANT_IDENTITY_ID,
      amount: Number(amount),
      paymentType: method as 'PIX',
      provider: provider as 'mercadoPago',
      currency: currency as 'brl',
      buyer: {
        name: user.name,
        isForeigner: user.isForeign,
        document: sanitizedCpf,
        email: user.email,
      },
    })
  }, [])

  const doMarketplacePayment = useCallback(async function doMarketplacePayment(
    sale: Sale
  ) {
    return await doMarketplacePurchase(sale.wallet, sale.offerId, sale.price)
  },
  [])

  const control = useAsyncControl({
    doPayment,
    doMarketplacePayment,
  })

  return control
}
